import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import { Link, useLocation } from 'react-router-dom'
import { List, Box, Typography, ListItemButton, ListItemText, ListItemIcon } from '@mui/material'
import { styled } from '@mui/material/styles'
import {
  People as PeopleIcon,
  Folder as FolderIcon,
  PowerSettingsNew as LogoutIcon,
  Equalizer as EqualizerIcon,
  ListAlt as ListAltIcon,
  Inventory as InventoryIcon,
  FactCheck as FactCheckIcon,
  History as HistoryIcon,
} from '@mui/icons-material'

import AuthContext from '../contexts/AuthContext'

// Styled Components
const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'isLogout',
})(({ theme, isSelected, isLogout }) => ({
  position: 'relative',
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(2),
  paddingTop: isLogout ? theme.spacing(1) : theme.spacing(1.5),
  paddingBottom: isLogout ? theme.spacing(1) : theme.spacing(1.5),
  marginBottom: isLogout ? 0 : theme.spacing(0.5),
  borderRadius: '0 8px 8px 0',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: isSelected ? '4px' : '0px',
    backgroundColor: isSelected ? theme.palette.primary.main : 'transparent',
    transition: theme.transitions.create(['width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  backgroundColor: isSelected
    ? theme.palette.mode === 'light'
      ? 'rgba(230, 237, 255, 1)'
      : 'rgba(52, 103, 246, 0.15)'
    : 'transparent',
  '&:hover': {
    backgroundColor: isSelected
      ? theme.palette.mode === 'light'
        ? 'rgba(230, 237, 255, 1)'
        : 'rgba(52, 103, 246, 0.15)'
      : theme.palette.mode === 'light'
        ? 'rgba(0, 0, 0, 0.04)'
        : 'rgba(255, 255, 255, 0.04)',
  },
  // Responsive styles for mobile
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1.5),
    paddingTop: isLogout ? theme.spacing(1) : theme.spacing(1.5),
    paddingBottom: isLogout ? theme.spacing(1) : theme.spacing(1.5),
    marginBottom: isLogout ? 0 : theme.spacing(0.5),
    borderRadius: '0 6px 6px 0',
    '&::before': {
      width: isSelected ? '3px' : '0px',
    },
  },
}))

const StyledListItemIcon = styled(ListItemIcon, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})(({ theme, isSelected }) => ({
  minWidth: 40,
  color: isSelected
    ? theme.palette.primary.main
    : theme.palette.mode === 'light'
      ? 'rgba(0, 0, 0, 0.54)'
      : 'rgba(255, 255, 255, 0.54)',
  transition: theme.transitions.create(['color'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  '& .MuiSvgIcon-root': {
    fontSize: '1.5rem',
  },
  // Responsive styles
  [theme.breakpoints.down('sm')]: {
    minWidth: 36,
    '& .MuiSvgIcon-root': {
      fontSize: '1.375rem',
    },
  },
  [theme.breakpoints.between('sm', 'md')]: {
    '& .MuiSvgIcon-root': {
      fontSize: '1.4375rem',
    },
  },
}))

const StyledListItemText = styled(ListItemText, {
  shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'isLogout',
})(({ theme, isSelected, isLogout }) => ({
  '& .MuiListItemText-primary': {
    color: isSelected
      ? theme.palette.primary.main
      : theme.palette.mode === 'light'
        ? 'rgba(0, 0, 0, 0.87)'
        : 'rgba(255, 255, 255, 0.87)',
    fontWeight: isSelected ? 600 : 400,
    fontSize: '0.875rem',
    transition: theme.transitions.create(['color', 'fontWeight'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  '& .MuiListItemText-secondary': {
    color: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.38)' : 'rgba(255, 255, 255, 0.38)',
    fontSize: '0.75rem',
    marginTop: isLogout ? theme.spacing(0.125) : theme.spacing(0.25),
    lineHeight: 1.3,
  },
  // Responsive styles
  [theme.breakpoints.down('sm')]: {
    '& .MuiListItemText-primary': {
      fontSize: '0.875rem',
      fontWeight: isSelected ? 600 : 500,
    },
    '& .MuiListItemText-secondary': {
      fontSize: '0.75rem',
      lineHeight: 1.2,
    },
  },
}))

// Menu Item Component
const MenuItem = ({
  path,
  title,
  icon,
  subtitle,
  onClick,
  isLogout = false,
  onItemClick = null,
  pathMatch = null,
}) => {
  const { pathname } = useLocation()
  // Support both exact path match and pattern match (for /event/:eventId routes)
  const isSelected = !isLogout && (pathMatch ? pathMatch.test(pathname) : pathname === path)

  const handleClick = (e) => {
    if (isLogout && onClick) {
      e.preventDefault()
      onClick()
    }
    // Close drawer on mobile when item is clicked
    if (onItemClick) {
      onItemClick()
    }
  }

  return (
    <StyledListItemButton
      component={isLogout ? 'div' : Link}
      to={isLogout ? '#' : path}
      selected={isSelected}
      isSelected={isSelected}
      isLogout={isLogout}
      onClick={handleClick}
      sx={{
        cursor: isLogout ? 'pointer' : 'default',
      }}>
      {icon && <StyledListItemIcon isSelected={isSelected}>{icon}</StyledListItemIcon>}
      <StyledListItemText
        primary={title}
        secondary={subtitle}
        isSelected={isSelected}
        isLogout={isLogout}
      />
    </StyledListItemButton>
  )
}

MenuItem.propTypes = {
  path: PropTypes.string.isRequired,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  icon: PropTypes.node,
  subtitle: PropTypes.string,
  onClick: PropTypes.func,
  isLogout: PropTypes.bool,
  onItemClick: PropTypes.func,
  pathMatch: PropTypes.instanceOf(RegExp),
}

// Main Sidebar Component
const Sidebar = ({ onItemClick = null, isMobile = false }) => {
  const { user, removeToken } = useContext(AuthContext)
  const { pathname } = useLocation()

  const logout = () => {
    removeToken()
  }

  // Check if we're on an event detail page (/event/:eventId or sub-routes)
  const isEventDetailPage = /^\/event\/[^/]+/.test(pathname)

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        backgroundColor: (theme) =>
          theme.palette.mode === 'dark'
            ? theme.palette.background.sidebar || theme.palette.background.default // Pure black in dark mode
            : theme.palette.background.paper,
      }}>
      {/* Racepack Header - Mobile Only */}
      {isMobile && (
        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 2, sm: 2.5, md: 3 },
            py: { xs: 1.5, sm: 2, md: 2.5 },
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' },
            }}>
            Racepack
          </Typography>
        </Box>
      )}

      {/* Main Menu Items - Scrollable */}
      <Box
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          pt: { xs: 1.5, sm: 2, md: 2.5 },
          pb: { xs: 1, sm: 1.5, md: 2 },
          px: { xs: 1, sm: 1.5, md: 2 },
          // Custom scrollbar styling - theme aware
          '&::-webkit-scrollbar': {
            width: { xs: '4px', sm: '6px', md: '6px' },
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.2)',
            borderRadius: { xs: '2px', sm: '3px', md: '3px' },
            '&:hover': {
              backgroundColor: (theme) =>
                theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.3)',
            },
          },
        }}>
        <List
          dense
          component="nav"
          sx={{
            width: '100%',
            py: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 0.25, sm: 0.5, md: 0.5 },
          }}>
          {isEventDetailPage ? (
            // Event Detail Page Menu Items
            <>
              {/* Extract eventId from pathname */}
              {(() => {
                const eventIdMatch = pathname.match(/^\/event\/([^/]+)/)
                const eventId = eventIdMatch ? eventIdMatch[1] : null
                if (!eventId) return null

                return (
                  <>
                    {/* Stats Menu Item */}
                    <MenuItem
                      path={`/event/${eventId}/stats`}
                      title="Stat"
                      icon={<EqualizerIcon />}
                      onItemClick={onItemClick}
                      pathMatch={/^\/event\/[^/]+\/stats/}
                    />
                    {/* Participants Menu Item - Also matches /event/:eventId (default route) */}
                    <MenuItem
                      path={`/event/${eventId}/participant`}
                      title="Participant"
                      icon={<ListAltIcon />}
                      onItemClick={onItemClick}
                      pathMatch={/^\/event\/[^/]+(\/participant|\/participant\/add|$)/}
                    />
                    {/* Stock Menu Item */}
                    <MenuItem
                      path={`/event/${eventId}/stock`}
                      title="Stock"
                      icon={<InventoryIcon />}
                      onItemClick={onItemClick}
                      pathMatch={/^\/event\/[^/]+\/stock/}
                    />
                    {/* Activity & Logs - Admin only, both pages guard themselves too */}
                    {user?.role === 'Admin' && (
                      <>
                        <MenuItem
                          path={`/event/${eventId}/activity`}
                          title="Activity"
                          icon={<FactCheckIcon />}
                          onItemClick={onItemClick}
                          pathMatch={/^\/event\/[^/]+\/activity/}
                        />
                        <MenuItem
                          path={`/event/${eventId}/logs`}
                          title="Logs"
                          icon={<HistoryIcon />}
                          onItemClick={onItemClick}
                          pathMatch={/^\/event\/[^/]+\/logs/}
                        />
                      </>
                    )}
                  </>
                )
              })()}
            </>
          ) : (
            // Default Menu Items
            <>
              {/* User Menu Item - Always show if user exists and not Staff */}
              {user && user?.role !== 'Staff' && (
                <MenuItem
                  path="/users"
                  title="User"
                  icon={<PeopleIcon />}
                  onItemClick={onItemClick}
                />
              )}
              {/* All Event Menu Item - Always show */}
              <MenuItem
                path="/events"
                title="All Event"
                icon={<FolderIcon />}
                onItemClick={onItemClick}
              />
              {/* Deletion Logs across all events - Admin only */}
              {user && user?.role === 'Admin' && (
                <MenuItem
                  path="/logs"
                  title="Logs"
                  icon={<HistoryIcon />}
                  onItemClick={onItemClick}
                />
              )}
            </>
          )}
        </List>
      </Box>

      {/* Logout Section - Fixed at bottom */}
      <Box
        sx={{
          flexShrink: 0,
          borderTop: '1px solid',
          borderColor: 'divider',
          pt: { xs: 1.5, sm: 2, md: 2 },
          pb: { xs: 1.5, sm: 2, md: 2 },
          px: { xs: 1, sm: 1.5, md: 2 },
          backgroundColor: 'background.paper',
        }}>
        <List
          dense
          component="nav"
          sx={{
            py: 0,
            width: '100%',
          }}>
          <MenuItem
            path="#"
            title="Logout"
            icon={<LogoutIcon />}
            subtitle="Logout your Account"
            onClick={logout}
            isLogout
            onItemClick={onItemClick}
          />
        </List>
      </Box>
    </Box>
  )
}

Sidebar.propTypes = {
  onItemClick: PropTypes.func,
  isMobile: PropTypes.bool,
}

export default Sidebar
